import React, { useCallback, useEffect, useRef, useState } from "react";
import ImageWrapper from "../ImageComponent/ImageWrapper";
import {
  formatAmountPutComma,
  parsePoojaDescriptionRawHTML,
} from "@/utils/utils";
import parse from "html-react-parser";

const Modal = ({
  onClose,
  selectedPackage,
  selectedPackageName,
  poojaDetails,
}) => {
  const modalRef = useRef(null);
  const [showAbsoluteDiv, setShowAbsoluteDiv] = useState(false);

  const handleClose = useCallback(() => {
    setShowAbsoluteDiv(false);
    setTimeout(() => {
      onClose();
    }, 200);
  }, [onClose]);

  const handleCloseRef = useRef(handleClose);
  useEffect(() => {
    handleCloseRef.current = handleClose;
  }, [handleClose]);

  const handleOutsideClick = useCallback(
    (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        handleClose();
      }
    },
    [handleClose]
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      // Restore body scroll when modal closes
      document.body.style.overflow = 'unset';
    };
  }, [handleOutsideClick]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setShowAbsoluteDiv(true);
    }, 50);

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    let statePushed = false;

    const handlePopState = (e) => {
      statePushed = false;
      handleCloseRef.current();
    };

    if (isMobile) {
      window.history.pushState({ ...window.history.state, isModal: true }, "", window.location.href);
      statePushed = true;
      window.addEventListener("popstate", handlePopState);
    }

    return () => {
      if (isMobile) {
        window.removeEventListener("popstate", handlePopState);
        if (statePushed) {
          window.history.back();
        }
      }
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50">
      <div className="flex flex-row justify-end h-full">
        <button
          onClick={handleClose}
          className={`${showAbsoluteDiv
              ? " hidden md:flex md:right-0 md:absolute"
              : " hidden md:flex md:-right-full md:absolute"
            } w-7 h-7 m-4 relative cursor-pointer z-10`}
        >
          <ImageWrapper
            src="/icons/CancelDesktop.svg"
            alt="Cancel button icon"
            className={`${showAbsoluteDiv ? "md:right-0" : "md:-right-full"
              } absolute object-cover cursor-pointer`}
          />
        </button>
        <div className="transition-all duration-500 md:w-1/2 lg:w-2/5 xl:w-1/3"></div>
        <div
          ref={modalRef}
          className={`absolute duration-200 flex flex-col text-Default bg-white shadow-lg rounded-t-2xl md:rounded-r-none md:rounded-l-3xl
            ${showAbsoluteDiv
              ? "top-6 sm:top-10 md:top-0 md:right-0"
              : "top-full md:top-0 md:-right-full"
            }
            w-full md:w-1/2 lg:w-2/5 xl:w-1/3
            h-[calc(100dvh-1.5rem)] sm:h-[calc(100dvh-2.5rem)] md:h-full
            min-h-0 overflow-hidden
          `}
        >
          {/* Mobile Close Button */}
          <div className="md:hidden absolute top-4 right-4 z-50">
            <button
              onClick={handleClose}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-slate-100 shadow-md border border-slate-200 cursor-pointer"
            >
              <ImageWrapper
                src="/icons/CancelDesktop.svg"
                alt="Cancel button icon"
                className="w-3.5 h-3.5 object-contain"
              />
            </button>
          </div>

          {/* Scrollable content container */}
          <div className="flex flex-col h-full min-h-0 overflow-y-auto scrollbar-hide">
            {/* Main content */}
            <div className="flex-1 min-h-0 pb-8 pt-2">
              <h1 className="text-xl text-Default pb-4 px-7 pt-12 md:pt-8 font-medium tracking-wider leading-8 pr-14 md:pr-7">
                {poojaDetails.name}
              </h1>
              <div className="flex flex-col px-8">
                <div className="flex flex-col gap-1 pb-7">
                  <h4 className="text-base text-[#B74502] font-medium leading-6">
                    {selectedPackageName}
                  </h4>
                  <h3 className="text-Default text-2xl font-medium leading-6 tracking-normal">
                    &#8377;{" "}
                    {selectedPackage.price
                      ? formatAmountPutComma(selectedPackage.price)
                      : "On Request"}
                  </h3>
                </div>
                <div className="flex flex-col gap-2">
                  <h5 className="text-sm font-medium leading-5">Inclusions-1</h5>
                  <div className="flex flex-col sm:flex-wrap sm:h-36 text-Default gap-4 mb-4 text-sm font-base md:leading-5 tracking-normal">
                    {selectedPackage.inclusions &&
                      selectedPackage.inclusions.length > 0 &&
                      selectedPackage.inclusions.map((item, key) => {
                        return (
                          <div
                            className="flex flex-row gap-2 justify-start items-center"
                            key={key}
                          >
                            <div className="relative flex-shrink-0">
                              {!Object.keys(item).includes("present") ||
                                (item.present && item.present === true) ? (
                                <ImageWrapper src="/icons/tick.svg" alt="Included" width={20} height={20} />
                              ) : (
                                <ImageWrapper src="/icons/cross.svg" alt="Not included" width={20} height={20} />
                              )}
                            </div>
                            <p className="flex flex-row text-xs leading-4">
                              {item.name}
                            </p>
                          </div>
                        );
                      })}
                  </div>
                </div>
                <div className="flex flex-col text-xs w-[200px] text-Default gap-2 pb-6">
                  {selectedPackage.numberOfDays && (
                    <p className="flex flex-row font-semibold justify-between pr-1">
                      No of Days: <span>{selectedPackage.numberOfDays}</span>
                    </p>
                  )}
                  {selectedPackage.numberOfPandits && (
                    <p className="flex flex-row font-semibold justify-between pr-1">
                      No of Pandits: <span>{selectedPackage.numberOfPandits}</span>
                    </p>
                  )}
                </div>

                <div className="flex flex-col w-full text-Default tracking-wider pb-6">
                  {selectedPackage.note && (
                    <p className="flex flex-row text-xl text-BluntRed font-bold">
                      Note&nbsp;:&nbsp;&nbsp; <span> {selectedPackage.note}</span>
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-6 pb-4 md:pb-0">
                  {selectedPackage.description &&
                    selectedPackage.description.length > 0 &&
                    selectedPackage.description.map((item, key) => (
                      <div
                        className="flex flex-col p-4 text-Default rounded-lg bg-[#CBD5E166]"
                        key={key}
                      >
                        <div className="flex justify-between">
                          {item.procedureName && (
                            <h3 className="font-medium text-sm leading-5">
                              {item.procedureName}
                            </h3>
                          )}
                          {item.duration && (
                            <h3 className="text-xs font-medium">
                              <span className="text-BluntRed">Duration</span>{" "}
                              {item.duration}
                            </h3>
                          )}
                        </div>
                        {item.shortDescription && (
                          <p className="text-sm text-[#B74502] pt-[2px] pb-2">
                            {item.shortDescription}
                          </p>
                        )}
                        {item.longDescription && (
                          <p className="text-xs text-justify leading-4">
                            {parse(
                              parsePoojaDescriptionRawHTML(item.longDescription)
                            )}
                          </p>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Modal;